import React from 'react';
import { Box, Paper, Typography, Avatar, TextField, Button } from '@mui/material';
import { Pets as PetIcon } from '@mui/icons-material';
import ReactMarkdown from 'react-markdown';
import { Message } from '../hooks/useChatMessages';

interface ChatMessageItemProps {
    message: Message;
    user: any; // Using any for simplicity as User type might be complex or from firebase/auth
    editingMessage: string | null;
    newMessage: string;
    setNewMessage: (message: string) => void;
    onConfirmEdit: (messageId: string, newContent: string) => void;
    setEditingMessage: (messageId: string | null) => void;
    onRoadmapTrigger: (module: any) => void;
}

const ChatMessageItem: React.FC<ChatMessageItemProps> = ({
    message,
    user,
    editingMessage,
    newMessage,
    setNewMessage,
    onConfirmEdit,
    setEditingMessage,
    onRoadmapTrigger
}) => {
    const formatTime = (date: Date | undefined): string => {
        if (!date) return '';
        try {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (e) {
            return '';
        }
    };

    const isAssistant = message.role === 'assistant';

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: isAssistant ? 'flex-start' : 'flex-end',
            mb: 2,
        }}>
            <Box sx={{
                display: 'flex',
                flexDirection: isAssistant ? 'row' : 'row-reverse',
                alignItems: 'flex-start',
                maxWidth: '85%',
                gap: 1.5,
            }}>
                <Avatar
                    sx={{
                        width: 36,
                        height: 36,
                        bgcolor: isAssistant ? 'primary.main' : 'secondary.main',
                        mt: 0.5
                    }}
                    src={!isAssistant ? user?.photoURL : undefined}
                >
                    {isAssistant ? (
                        <img
                            src="/logo192.png"
                            alt="PetSnap"
                            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                        />
                    ) : (
                        <PetIcon />
                    )}
                </Avatar>

                <Paper
                    elevation={0}
                    sx={{
                        p: 2,
                        bgcolor: isAssistant ? 'var(--petsnap-surface)' : 'var(--petsnap-honey)',
                        color: isAssistant ? 'var(--petsnap-forest)' : '#FFFFFF',
                        borderRadius: 3,
                        borderTopLeftRadius: isAssistant ? 0 : 3,
                        borderTopRightRadius: isAssistant ? 3 : 0,
                        border: isAssistant ? '1px solid rgba(244, 162, 97, 0.2)' : 'none',
                        boxShadow: isAssistant
                            ? '0 2px 12px rgba(38, 70, 83, 0.06)'
                            : '0 4px 12px rgba(244, 162, 97, 0.25)',
                        position: 'relative',
                        animation: 'fadeUp 0.3s ease-out',
                    }}
                    onDoubleClick={() => {
                        if (!isAssistant) {
                            setEditingMessage(message.id);
                            setNewMessage(message.content);
                        }
                    }}
                >
                    {editingMessage === message.id ? (
                        <TextField
                            fullWidth
                            multiline
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onBlur={() => {
                                onConfirmEdit(message.id, newMessage);
                                setNewMessage('');
                            }}
                            autoFocus
                            variant="outlined"
                            size="small"
                            sx={{
                                bgcolor: '#FFFFFF',
                                borderRadius: 1,
                                '& .MuiOutlinedInput-root': {
                                    color: 'var(--petsnap-forest)',
                                },
                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'var(--petsnap-honey)' }
                            }}
                        />
                    ) : (
                        <Box sx={{
                            '& strong': { color: isAssistant ? 'var(--petsnap-terracotta)' : 'inherit', fontWeight: 700 },
                            '& code': { fontFamily: 'JetBrains Mono', bgcolor: 'rgba(0,0,0,0.08)', px: 0.5, borderRadius: 0.5 }
                        }}>
                            {isAssistant ? (
                                <ReactMarkdown
                                    components={{
                                        p: ({ children }) => <Typography variant="body1" sx={{ mb: 1.5, lineHeight: 1.7, color: 'var(--petsnap-forest)' }}>{children}</Typography>,
                                        h1: ({ children }) => <Typography variant="h4" sx={{ mb: 2, mt: 3, color: 'var(--petsnap-forest)', fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>{children}</Typography>,
                                        h2: ({ children }) => <Typography variant="h5" sx={{ mb: 2, mt: 3, color: 'var(--petsnap-forest)', fontWeight: 700, fontFamily: "'Outfit', sans-serif" }}>{children}</Typography>,
                                        h3: ({ children }) => <Typography variant="h6" sx={{ mb: 2, mt: 2, color: 'var(--petsnap-forest)', fontWeight: 600, fontFamily: "'Outfit', sans-serif" }}>{children}</Typography>,
                                        ul: ({ children }) => <Box component="ul" sx={{ pl: 2, mb: 2, '& li': { mb: 1, color: 'var(--petsnap-forest)' } }}>{children}</Box>,
                                        ol: ({ children }) => <Box component="ol" sx={{ pl: 2, mb: 2, '& li': { mb: 1, color: 'var(--petsnap-forest)' } }}>{children}</Box>,
                                        blockquote: ({ children }) => (
                                            <Box sx={{ borderLeft: 3, borderColor: 'var(--petsnap-honey)', pl: 2, ml: 1, mb: 2, fontStyle: 'italic', bgcolor: 'rgba(244, 162, 97, 0.08)', py: 1, borderRadius: '0 8px 8px 0', color: 'var(--petsnap-forest)' }}>
                                                {children}
                                            </Box>
                                        ),
                                        pre: ({ children }) => (
                                            <Box component="pre" sx={{
                                                backgroundColor: 'rgba(38, 70, 83, 0.05)',
                                                p: 2,
                                                borderRadius: 1.5,
                                                overflow: 'auto',
                                                fontFamily: 'JetBrains Mono',
                                                fontSize: '0.85rem',
                                                border: '1px solid rgba(38, 70, 83, 0.1)',
                                                mb: 2,
                                                color: 'var(--petsnap-forest)'
                                            }}>
                                                {children}
                                            </Box>
                                        )
                                    }}
                                >
                                    {message.content}
                                </ReactMarkdown>
                            ) : (
                                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: 'white', fontWeight: 500, fontFamily: "'Inter', sans-serif" }}>
                                    {message.content}
                                </Typography>
                            )}

                            {/* Roadmap Module Trigger */}
                            {message.module && isAssistant && (
                                <Box sx={{ mt: 3 }}>
                                    <Button
                                        variant="contained"
                                        onClick={() => onRoadmapTrigger(message.module)}
                                        sx={{
                                            borderRadius: 2,
                                            textTransform: 'none',
                                            fontWeight: 700,
                                            background: 'var(--gradient-golden)',
                                            boxShadow: '0 4px 12px rgba(244, 162, 97, 0.3)',
                                            color: '#FFFFFF',
                                            fontFamily: "'Outfit', sans-serif",
                                            '&:hover': {
                                                filter: 'brightness(1.05)',
                                                boxShadow: '0 8px 20px rgba(244, 162, 97, 0.4)',
                                            }
                                        }}
                                    >
                                        🗺️ {(() => {
                                            const isChinese = /[\u4e00-\u9fff]/.test(message.content);
                                            const isSpanish = /[áéíóúñü]/.test(message.content.toLowerCase());
                                            const isFrench = /[àâäéèêëïîôöùûüÿç]/.test(message.content.toLowerCase());

                                            const customTitle = message.module.title ||
                                                (message.module.roadmapData && message.module.roadmapData.title) ||
                                                '';

                                            if (isChinese) return customTitle ? `开始「${customTitle}」` : '开始宠物护理路径';
                                            if (isSpanish) return customTitle ? `Comenzar "${customTitle}"` : 'Comenzar Ruta';
                                            if (isFrench) return customTitle ? `Commencer "${customTitle}"` : 'Commencer';
                                            return customTitle ? `Start "${customTitle}"` : 'Start Path';
                                        })()}
                                    </Button>
                                </Box>
                            )}

                            <Typography
                                variant="caption"
                                sx={{
                                    color: isAssistant ? 'var(--text-secondary)' : 'rgba(255, 255, 255, 0.8)',
                                    mt: 1.5,
                                    display: 'block',
                                    fontWeight: 500
                                }}
                            >
                                {formatTime(message.createdAt)}
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default ChatMessageItem;
